import { Button, Stack, Typography } from '@mui/material'
import { JourneyCategory } from '@planning/app/api'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Header, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { BreadcrumpItem } from 'modules/ui/src/theme/components/breadcrumbs/Breadcrumb'
import { FC, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'

type Props = {
  vesselVisitItem: IVesselVisitItem
  direction: 'load' | 'discharge' | 'restow'
  category: JourneyCategory
  onSave: () => void
  onCancel: () => void
}

export const VesselVisitBulkEditHeader: FC<Props> = observer(
  ({ vesselVisitItem, category, direction, onSave, onCancel }) => {
    const { t } = useTranslate()
    const navigate = useNavigate()
    const theme = useMinimalsTheme()

    const vessel = computed(() => vesselVisitItem.vessels?.[0]?.data).get()

    const directionLabel = useMemo(() => {
      switch (direction) {
        case 'load':
          return t('loadList', 'Load List')
        case 'discharge':
          return t('dischargeList', 'Discharge List')
        case 'restow':
          return t('restowList', 'Restow List')
      }
    }, [direction, t])

    const title = useMemo(
      () =>
        vessel
          ? `${vessel.name} <${vessel.shippingLine}> - ${t('bulkEdit', 'Bulk edit')}`
          : t('bulkEdit', 'Bulk edit'),
      [vessel, t],
    )

    const subtitle = t(category)

    const getBreadcrumbs = () => {
      if (!vesselVisitItem || !vessel) return []

      const basePath = PATHS.visits.vesselVisits.url
      const carrierVisitDashboardUrl = `${basePath}/${vesselVisitItem.id}/dashboard`
      const directionPath = `${basePath}/${vesselVisitItem.id}/${direction}`

      const vesselVisitLabel = t('vesselVisits', 'Vessel visits')
      const carrierVisitLabel = `${vessel.name} <${vessel.shippingLine}>`

      const breadcrumbs: BreadcrumpItem[] = [
        {
          label: vesselVisitLabel,
          onClick: () => navigate(basePath),
        },
        {
          label: carrierVisitLabel,
          onClick: () => navigate(carrierVisitDashboardUrl),
        },
        {
          label: directionLabel,
          onClick: () => navigate(directionPath),
        },
        {
          label: t('bulkEdit', 'Bulk edit'),
        },
      ]

      return breadcrumbs
    }

    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <Breadcrumbs items={getBreadcrumbs()} />
        <Header
          position='relative'
          leftRenderOption={
            <Stack flex='none'>
              <Typography variant='h6'>{t('bulkEdit', 'Bulk edit')}</Typography>
              <Typography variant='caption' color={theme.palette.text.secondary} fontWeight={600}>
                {subtitle}
              </Typography>
            </Stack>
          }
          rightRenderOption={
            <Stack
              flexDirection='row'
              alignItems='center'
              justifyContent='end'
              width='100%'
              gap={2}
            >
              <Button
                variant='text'
                color='inherit'
                onClick={onCancel}
                data-cy='bulk-edit-cancel-btn'
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                data-cy='bulk-edit-save-btn'
                onClick={onSave}
              >
                {t('save', 'Save')}
              </Button>
            </Stack>
          }
        />
      </>
    )
  },
)
